/**
 * Hook to load grupos (dimensions) for a specific questionário
 *
 * Used by Template Builder to generate dynamic variable list
 * Groups are identified by their ordemQuestionario (1-indexed)
 *
 * Variable syntax: {{grupo[ordem].propriedade}}
 */

import { useQuery } from "@tanstack/react-query";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "@/base";
import useUserStore from "@/modules/users/store";

const QUESTIONARIO_GRUPOS_QUERY_KEY = "questionario-grupos";

/**
 * Feedback range for grupo score
 */
export interface GrupoFeedback {
  title: string;
  description: string;
  start: number;
  end: number;
}

export interface GrupoForVariable {
  id: string;
  nome: string;
  descricao: string;
  /** ordemQuestionario - used as index in template variables (1-indexed) */
  ordem: number;
  /** Feedbacks configured for this grupo (score-based) */
  feedbacks: GrupoFeedback[];
}

/**
 * Hook para carregar grupos de um questionário específico
 * @param questionarioId - ID do questionário
 * @returns Query com lista de grupos ordenados por ordemQuestionario
 */
export function useQuestionarioGroups(questionarioId?: string) {
  const empresaId = useUserStore((state) => state.activeEmpresaId);
  const licenciadaId = useUserStore((state) => state.activeLicenciadaId);

  const gruposQuery = useQuery({
    queryKey: [QUESTIONARIO_GRUPOS_QUERY_KEY, licenciadaId, empresaId, questionarioId],
    queryFn: async (): Promise<GrupoForVariable[]> => {
      console.log("[useQuestionarioGroups] queryFn called:", { licenciadaId, empresaId, questionarioId });

      if (!licenciadaId || !empresaId || !questionarioId) {
        console.log("[useQuestionarioGroups] Missing licenciadaId, empresaId or questionarioId");
        return [];
      }

      try {
        // Reference to grupos collection - using correct path with licenciada
        const gruposReference = collection(
          firestore,
          "licenciadas",
          licenciadaId,
          "empresas",
          empresaId,
          "gruposDiagnostico"
        );

        // Query grupos for this questionário, active only (without orderBy to avoid index issues)
        const q = query(gruposReference, where("idQuestionario", "==", questionarioId), where("deletedAt", "==", null));

        const snapshot = await getDocs(q);
        console.log("[useQuestionarioGroups] Query returned", snapshot.docs.length, "documents");

        // Map grupos - require ordemQuestionario >= 1 AND radar === true
        // IMPORTANT: Must match the filter used in useReportData for PDF generation
        const grupos: GrupoForVariable[] = [];

        for (const document_ of snapshot.docs) {
          const data = document_.data();
          console.log("[useQuestionarioGroups] Document:", document_.id, {
            nome: data.nome,
            ordemQuestionario: data.ordemQuestionario,
            radar: data.radar,
            idQuestionario: data.idQuestionario
          });

          // Only include grupos with valid ordemQuestionario (>= 1) AND radar === true
          // The radar filter MUST match useReportData to ensure consistent indexing
          const ordem = data.ordemQuestionario;
          if (!ordem || ordem < 1) continue;
          if (!data.radar) continue; // Skip grupos not visible in radar chart

          grupos.push({
            id: document_.id,
            nome: data.nome || "",
            descricao: data.descricao || "",
            ordem,
            feedbacks: data.feedbacks || []
          });
        }

        // Sort in memory by ordemQuestionario
        const sortedGrupos = grupos.sort((a, b) => a.ordem - b.ordem);

        // Reassign ordem to be 1-indexed position in the filtered/sorted array
        // This matches how variable-replacer accesses grupos: data.grupos[index - 1]
        return sortedGrupos.map((grupo, index) => ({
          ...grupo,
          ordem: index + 1 // 1-indexed position in array
        }));
      } catch (error) {
        console.error("[useQuestionarioGroups] Error:", error);
        throw error;
      }
    },
    enabled: !!licenciadaId && !!empresaId && !!questionarioId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2
  });

  return {
    grupos: gruposQuery.data ?? [],
    isLoading: gruposQuery.isLoading,
    error: gruposQuery.error,
    refetch: gruposQuery.refetch
  };
}
